// src/api/configs/axiosConfigs.js
import axios from "axios";

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: import.meta.env.PROD ? 15000 : 5000 * 60,
  withCredentials: false, // Auth0 SPA no requiere cookie httpOnly
  headers: {
    Accept: "application/json"
  }
});

// Proveedor de token (get/refresh). Lo inyectas desde tu capa de Auth0.
let tokenProvider = {
  get: async () => null, // debe devolver string | null
  refresh: async () => null // debe devolver string | null (forzar renovación)
};

export const setAccessTokenProvider = ({ get, refresh }) => {
  if (typeof get === "function") tokenProvider.get = get;
  if (typeof refresh === "function") tokenProvider.refresh = refresh;
};

// Evita múltiples refresh simultáneos
let refreshInFlight = null;

// Callback global para manejar sesión expirada (se configura desde Auth0TokenConnector)
let onSessionExpiredCallback = null;

// Configurar callback para manejar sesión expirada
export const setSessionExpiredHandler = (callback) => {
  onSessionExpiredCallback = callback;
};

// Inyecta access token antes de cada request
instance.interceptors.request.use(
  async config => {
    try {
      const token = await tokenProvider.get();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // si falla obtener token, sigue sin header
    }
    return config;
  },
  error => Promise.reject(error)
);

// Reintento automático en 401/400 (errores de autenticación) con renovación silenciosa (una sola en paralelo)
instance.interceptors.response.use(
  r => r,
  async error => {
    const original = error.config || {};
    // Verificar que existe error.response antes de acceder a status
    const status = error.response?.status;
    
    // Verificar si es un error de autenticación (401 o 400 con InvalidRequestError)
    const isAuthError = status === 401 || 
      (status === 400 && (
        error.response?.data?.message?.includes('InvalidRequestError') ||
        error.response?.data?.code === '' ||
        error.message?.includes('InvalidRequestError')
      ));

    // Solo reintenta una vez por request y únicamente en errores de autenticación
    if (isAuthError && !original._retry) {
      original._retry = true;

      // Si ya hay un refresh corriendo, espera a que termine
      if (!refreshInFlight) {
        refreshInFlight = (async () => {
          try {
            const newToken = await tokenProvider.refresh();
            return newToken || null;
          } finally {
            // libera el lock al finalizar (éxito o error)
            const done = refreshInFlight;
            refreshInFlight = null;
            return done;
          }
        })();
      }

      // Espera el resultado del refresh en curso
      const maybePromise = refreshInFlight;
      const result = await maybePromise;
      const newToken = typeof result === "string" ? result : null;

      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        // Reintenta la solicitud original con el nuevo token
        return instance(original);
      }
      
      // Si no se obtuvo token, la sesión expiró
      // Verificar si el callback está configurado y llamarlo
      if (onSessionExpiredCallback && typeof onSessionExpiredCallback === 'function') {
        try {
          // Verificar si todavía está autenticado antes de hacer logout
          // Esto evita hacer logout durante el flujo de login
          if (checkAuth0Authenticated && typeof checkAuth0Authenticated === 'function') {
            const isStillAuthenticated = checkAuth0Authenticated();
            if (isStillAuthenticated) {
              onSessionExpiredCallback();
            }
          } else {
            // Si no hay checker, asumir que está autenticado y hacer logout
            onSessionExpiredCallback();
          }
        } catch (err) {
          // Si hay error verificando, aún así intentar logout por seguridad
          console.error('Error verificando autenticación:', err);
          onSessionExpiredCallback();
        }
      }
      
      // Si no se obtuvo token, deja caer al reject
    }

    return Promise.reject(error);
  }
);

// Función helper para verificar si Auth0 todavía considera al usuario autenticado
// Se configura desde Auth0TokenConnector
let checkAuth0Authenticated = null;

export const setAuth0AuthenticatedChecker = (checker) => {
  checkAuth0Authenticated = checker;
};

// Helper principal
export async function apiCall(config) {
  try {
    const res = await instance.request(config);
    return {
      ...res.data,
      statusCode: res.status
    };
  } catch (error) {
    // Si hay una respuesta del servidor, devolver el contenido aunque sea error
    if (error.response && error.response.data) {
      return {
        ...error.response.data,
        success: false,
        statusCode: error.response.status
      };
    }
    // Si no hay respuesta, lanzar el error original
    throw error;
  }
}

export default instance;
